import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { REST_API_URL } from "../../../api/config";
import { useAppSelector } from "../../../hooks/redux/useRedux";
import { selectUser } from "../../../redux/slices/profileSlice/ProfileSlice";
import { PrimaryButton } from "../../buttons/primaryButton/PrimaryButton";

interface IStories {
  id: number;
  title: string;
  icon_image: string;
  items: IStoriesItem[];
  order: number;
  navigation_button: boolean;
  button_text: string;
  base_navigation: string | null;
  website_navigation: string | null;
}

interface IStoriesItem {
  id: number;
  title: string;
  description: string;
  background_image: string;
}

interface IStoriesModalProps {
  showStory: any;
  setShowStory: (showStory: null | number) => void;
  stories: IStories[] | null;
  hasSubscription?: boolean;
  setShowSubModal?: (show: boolean) => void;
  setIsWhatsAppModalOpen?: (show: boolean) => void;
}

export const StoriesModal = ({
  showStory,
  setShowStory,
  stories,
  hasSubscription = false,
  setShowSubModal,
  setIsWhatsAppModalOpen,
}: IStoriesModalProps) => {
  const [activeGroupIndex, setActiveGroupIndex] = useState(0);
  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const user = useAppSelector(selectUser);
  const showStoryId = showStory?.id || null;

  // Initialize active group/item when modal is opened
  useEffect(() => {
    if (showStory && stories) {
      const idStr = String(showStory.id);
      const parts = idStr.split("-");
      const blockId = parseInt(parts[0]);
      const itemIndex = parts.length > 1 ? parseInt(parts[1]) : 0;

      const groupIdx = stories.findIndex((g) => g.id === blockId);
      if (groupIdx !== -1) {
        setActiveGroupIndex(groupIdx);
        setActiveItemIndex(itemIndex);
      } else {
        setActiveGroupIndex(0);
        setActiveItemIndex(0);
      }
      setProgress(0);
      setIsPaused(false);
    }
  }, [showStoryId, stories]);

  // Handle automatic progress & autoplay
  useEffect(() => {
    if (!showStory || !stories || isPaused) return;

    const currentGroup = stories[activeGroupIndex];
    if (!currentGroup) return;

    const intervalTime = 50; // ms
    const duration = 10000; // 10000ms duration per story
    const step = (intervalTime / duration) * 100;

    // Decouple progress calculation from React state queue to prevent race conditions
    let currentProgress = 0;
    setProgress(0);

    const timer = setInterval(() => {
      currentProgress += step;
      if (currentProgress >= 100) {
        clearInterval(timer);
        setProgress(100);
        setTimeout(() => {
          handleNext();
        }, 0);
      } else {
        setProgress(currentProgress);
      }
    }, intervalTime);

    return () => clearInterval(timer);
  }, [showStoryId, stories, activeGroupIndex, activeItemIndex, isPaused]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showStory) return;
      if (e.key === "Escape") {
        setShowStory(null);
      } else if (e.key === "ArrowLeft") {
        handlePrev();
      } else if (e.key === "ArrowRight") {
        handleNext();
      } else if (e.key === " ") {
        e.preventDefault();
        setIsPaused((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showStory, stories, activeGroupIndex, activeItemIndex]);

  const handleNext = () => {
    if (!stories) return;
    const currentGroup = stories[activeGroupIndex];
    const itemsCount = currentGroup?.items?.length || 1;

    if (activeItemIndex < itemsCount - 1) {
      setActiveItemIndex((prev) => prev + 1);
      setProgress(0);
    } else {
      // Transition to next group
      if (activeGroupIndex < stories.length - 1) {
        setActiveGroupIndex((prev) => prev + 1);
        setActiveItemIndex(0);
        setProgress(0);
      } else {
        // Last story group finished, close modal
        setShowStory(null);
      }
    }
  };

  const handlePrev = () => {
    if (!stories) return;

    if (activeItemIndex > 0) {
      setActiveItemIndex((prev) => prev - 1);
      setProgress(0);
    } else {
      // Transition to previous group
      if (activeGroupIndex > 0) {
        const prevGroup = stories[activeGroupIndex - 1];
        const prevItemsCount = prevGroup?.items?.length || 1;
        setActiveGroupIndex((prev) => prev - 1);
        setActiveItemIndex(prevItemsCount - 1);
        setProgress(0);
      } else {
        // First item of first group: restart current item
        setProgress(0);
      }
    }
  };

  const handlePrevGroup = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeGroupIndex > 0) {
      setActiveGroupIndex((prev) => prev - 1);
      setActiveItemIndex(0);
      setProgress(0);
    }
  };

  const handleNextGroup = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (stories && activeGroupIndex < stories.length - 1) {
      setActiveGroupIndex((prev) => prev + 1);
      setActiveItemIndex(0);
      setProgress(0);
    }
  };

  const handleCtaAction = (group: IStories, item: any) => {
    const requiresSubscription =
      group.base_navigation === "instagram" ||
      group.base_navigation === "whatsapp";

    if (requiresSubscription && !hasSubscription) {
      if (setShowSubModal) setShowSubModal(true);
      return;
    }

    const API_URL = REST_API_URL;

    const instagramUrl = (emailStr: string) =>
      `https://www.instagram.com/oauth/authorize?enable_fb_login=0&force_authentication=1&disable_native=1&client_id=1362107724965082&redirect_uri=${API_URL}/auth/connect-instagram&response_type=code&scope=instagram_business_basic,instagram_business_manage_messages&state=${encodeURIComponent(
        emailStr,
      )}|||web`;

    const googleCalendarUrl = (email: string) => {
      const redirectUri = encodeURIComponent(
        `${window.location.origin}/profile`,
      );
      return `${API_URL}/calendar/google/calendar-init?state=${encodeURIComponent(email + "|||web")}&redirect_uri=${redirectUri}`;
    };

    if (item.website_navigation) {
      window.open(item.website_navigation, "_blank");
    } else if (group.website_navigation) {
      window.open(group.website_navigation, "_blank");
    } else if (group.base_navigation === "instagram") {
      window.location.href = instagramUrl(user.user.email);
    } else if (group.base_navigation === "whatsapp") {
      setShowStory(null);
      setIsWhatsAppModalOpen?.(true);
    } else if (group.base_navigation === "google_calendar") {
      window.location.href = googleCalendarUrl(user.user.email);
    }
  };

  return (
    <AnimatePresence>
      {showStory && stories && (
        <motion.div
          className="fixed inset-0 bg-[#0f0f11]/95 backdrop-blur-md flex items-center justify-center z-50 h-full w-full select-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Brand Logo */}
          <h2 className="absolute top-6 left-6 text-white font-serif text-[24px] font-bold tracking-widest pointer-events-none drop-shadow">
            HALPER
          </h2>

          {/* Close button */}
          <PrimaryButton
            onClick={() => setShowStory(null)}
            label={<X className="w-6 h-6" />}
            size="small"
            variant="ghost"
            className="absolute top-6 right-6 text-white/70 hover:text-white z-[60] bg-white/5 !p-2 rounded-full hover:bg-white/10"
          />

          {/* Stories Slides Container */}
          <div className="relative w-full h-[90vh] flex items-center justify-center overflow-hidden">
            {/* Active Card Slider Track */}
            <motion.div
              className="flex items-center gap-6 absolute left-0 h-full"
              animate={{
                x: `calc(50vw - (${activeGroupIndex} * (min(400px, 92vw) + 24px)) - (min(400px, 92vw) / 2 + 20px))`,
              }}
              transition={{ type: "spring", stiffness: 220, damping: 24 }}
            >
              {stories.map((group, gIdx) => {
                const isActive = gIdx === activeGroupIndex;
                const items = group.items?.length
                  ? group.items
                  : [
                      {
                        id: group.id,
                        title: group.title,
                        description: "",
                        background_image: group.icon_image,
                      },
                    ];

                const displayItemIdx = isActive ? activeItemIndex : 0;
                const currentItem = items[displayItemIdx] || items[0];

                return (
                  <motion.div
                    key={group.id}
                    className="relative rounded-2xl overflow-hidden shadow-2xl flex-shrink-0 cursor-pointer select-none w-[min(400px,92vw)] h-[700px] max-h-[85vh]"
                    animate={{
                      scale: isActive ? 1.0 : 0.8,
                      opacity: isActive ? 1.0 : 0.45,
                    }}
                    onClick={() => {
                      if (!isActive) {
                        setActiveGroupIndex(gIdx);
                        setActiveItemIndex(0);
                        setProgress(0);
                      }
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Background story image */}
                    <img
                      key={`${group.id}-${currentItem.id || "cover"}-${displayItemIdx}`}
                      src={currentItem.background_image || group.icon_image}
                      alt={currentItem.title}
                      className="w-full h-full object-cover select-none pointer-events-none absolute inset-0"
                    />

                    {/* Top & Bottom gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70 pointer-events-none" />

                    {/* INACTIVE STATE CARD CONTENT */}
                    {!isActive && (
                      <div className="absolute inset-0 bg-black/45 flex flex-col items-center justify-center p-4 text-center z-10">
                        <div className="w-16 h-16 rounded-full border-2 border-[#020637] p-0.5 mb-3 bg-neutral-900/90 shadow-md">
                          <img
                            src={group.icon_image}
                            alt={group.title}
                            className="w-full h-full object-cover rounded-full"
                          />
                        </div>
                        <h4 className="text-white font-bold text-sm drop-shadow">
                          {group.title}
                        </h4>
                      </div>
                    )}

                    {/* ACTIVE STATE CARD CONTENT */}
                    {isActive && (
                      <div className="absolute inset-0 flex flex-col justify-between p-3.5 z-10">
                        {/* Header & Progress Indicators */}
                        <div>
                          {/* Segmented Progress Bars */}
                          <div className="flex gap-1.5 mb-3">
                            {items.map((_, iIdx) => (
                              <div
                                key={iIdx}
                                className="h-1 flex-1 bg-white/25 rounded-full overflow-hidden"
                              >
                                <div
                                  className="h-1 bg-white rounded-full transition-all duration-75 ease-out"
                                  style={{
                                    width:
                                      iIdx < activeItemIndex
                                        ? "100%"
                                        : iIdx === activeItemIndex
                                          ? `${progress}%`
                                          : "0%",
                                  }}
                                />
                              </div>
                            ))}
                          </div>

                          {/* Story Header details */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-9 h-9 rounded-full border border-white/40 p-0.5 shadow-sm">
                                <img
                                  src={group.icon_image}
                                  alt={group.title}
                                  className="w-full h-full object-cover rounded-full"
                                />
                              </div>
                              <span className="text-white font-bold text-sm drop-shadow">
                                {group.title}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Tap areas for next/prev inside the active card */}
                        <div className="absolute inset-x-0 top-16 bottom-24 flex pointer-events-auto z-0">
                          <div
                            className="w-[30%] h-full cursor-w-resize"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePrev();
                            }}
                          />
                          <div
                            className="w-[70%] h-full cursor-e-resize"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNext();
                            }}
                          />
                        </div>

                        {/* Text Caption overlay */}
                        <div className="mt-auto mb-4 pointer-events-none drop-shadow z-10 px-0.5">
                          <h3 className="text-white font-extrabold text-[15px]">
                            {currentItem.title}
                          </h3>
                          {currentItem.description && (
                            <p className="text-white/95 text-xs mt-1 line-clamp-3 leading-relaxed font-medium">
                              {currentItem.description}
                            </p>
                          )}
                        </div>

                        {/* Reply / Interaction Footer */}
                        <div className="flex flex-col gap-3 pointer-events-auto z-10">
                          {/* CTA button if applicable */}
                          {(group.navigation_button ||
                            group.website_navigation ||
                            group.base_navigation) && (
                            <PrimaryButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCtaAction(group, currentItem);
                              }}
                              label={group.button_text}
                              size="small"
                              className="w-full justify-center bg-white !text-black rounded-xl text-xs font-bold hover:bg-neutral-100 shadow-lg"
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Active Group Navigation Arrows (left/right of the active card) */}
                    {isActive && (
                      <>
                        {activeGroupIndex > 0 && (
                          <PrimaryButton
                            onClick={(e?: any) => handlePrevGroup(e)}
                            label={<ChevronLeft className="w-5 h-5" />}
                            size="small"
                            variant="ghost"
                            className="absolute -left-14 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 !text-white z-40 border border-white/5 hover:scale-105 shadow-md backdrop-blur-sm !p-0 justify-center"
                          />
                        )}
                        {activeGroupIndex < stories.length - 1 && (
                          <PrimaryButton
                            onClick={(e?: any) => handleNextGroup(e)}
                            label={<ChevronRight className="w-5 h-5" />}
                            size="small"
                            variant="ghost"
                            className="absolute -right-14 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 !text-white z-40 border border-white/5 hover:scale-105 shadow-md backdrop-blur-sm !p-0 justify-center"
                          />
                        )}
                      </>
                    )}
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
 

